import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import {
  DirectionGroupFormDto,
  type DirectionGroupFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/direction/direction-group-form.dto';
import { DirectionTypeEnum } from '@zaoblako/shared/modules/catalog/enums/direction.type.enum';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ColorPickerComponent } from '@zaoblako/web/shared/dumbs/color-picker/color-picker.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UserAndGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-and-group-multi-select/user-and-group-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-direction-group-form',
  templateUrl: './direction-group-form.component.html',
  styleUrls: ['./direction-group-form.component.scss'],
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SingleSelectComponent,
    DirectionSingleSelectComponent,
    TagModule,
    ButtonComponent,
    MultiSelectComponent,
    SpinnerComponent,
    DirectoryCommonHistoryComponent,
    UserAndGroupMultiSelectComponent,
    ColorPickerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DirectionGroupFormComponent extends BaseFormComponent<DirectionGroupFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public direction = { color: null } as DirectionFindInterface;

  public dto = DirectionGroupFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public directions: DirectionFindInterface[] = [];

  public types = Object.values(DirectionTypeEnum);

  public directionGroupUsers = [];

  public get DirectionTypeEnum() {
    return DirectionTypeEnum;
  }

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  public constructor(
    public readonly directionService: DirectionService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.directionService.find<DirectionFindInterface>().subscribe((directions) => {
      this.directions = directions;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.directionService
      .findById<DirectionFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (direction) => {
          this.direction = direction;
          if (this.direction.users?.length) {
            this.direction.users.forEach((user) => this.directionGroupUsers.push({ user }));
          }
          if (this.direction.userGroups?.length) {
            this.direction.userGroups.forEach((group) => this.directionGroupUsers.push({ group }));
          }
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveDirection() {
    this.normalizeDirectionColor();

    if (this.checkValidate(this.direction as DirectionGroupFormInterface).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.directionService.update<DirectionGroupFormInterface, DirectionFindInterface>(this.direction).subscribe({
          next: (direction) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Бюджетная статья "${this.direction.name}" обновлена`);
            this.formStoreService.saveNext(direction, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.directionService.createGroup(this.direction).subscribe({
          next: (direction) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Группа БС "${this.direction.name}" создана`);
            this.formStoreService.saveNext(direction, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteDirection() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить бюджетную статью "${this.direction.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.directionService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Бюджетная статья "${this.direction.name}" удалена`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreDirection() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить статью "${this.direction.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.directionService.restore(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.direction = { ...this.direction, deleted: false };
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Статья "${this.direction.name}" восстановлена`);
            this.formStoreService.saveNext(this.direction, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public onChangeUsersAndGroup(): void {
    this.direction.users = [];
    this.direction.userGroups = [];
    this.directionGroupUsers.forEach((directionUser) => {
      directionUser.user
        ? this.direction.users.push(directionUser.user)
        : this.direction.userGroups.push(directionUser.group);
    });
  }

  public changeColor(color: string | null | undefined): void {
    const value = color?.trim() ?? '';
    if (!value) {
      this.direction.color = null;
      this.cdRef.markForCheck();
      return;
    }

    this.direction.color = value[0] !== '#' ? `#${value}` : value;
    this.cdRef.markForCheck();
  }

  public onColorPickerChange(color: string): void {
    const value = color?.trim() ?? '';
    if (!value) {
      this.direction.color = null;
    } else {
      this.direction.color = value[0] !== '#' ? `#${value}` : value;
    }
    this.cdRef.markForCheck();
  }

  private normalizeDirectionColor(): void {
    if (this.direction.parent) {
      this.direction.color = null;
      return;
    }

    const trimmed = this.direction.color?.trim();
    if (!trimmed) {
      this.direction.color = null;
      return;
    }

    this.direction.color = trimmed[0] !== '#' ? `#${trimmed}` : trimmed;
  }
}
